import { Instance, Options } from "@popperjs/core";
import { css } from "@emotion/react";
import { usePopper } from "react-popper";
import React, { useCallback, useState } from "react";
import { Portal, PortalProps } from "../Portal";
import { useOnClickOutside } from "../hooks/useOnClickOutside";
import { useTheme } from "../theme";

export type PopperInstance = Instance;
export type PopperOptions = Partial<Omit<Options, "placement">>;
export type PopperPlacement = Options["placement"];

export interface PopperBaseProps {
  /**
   * Popperの起点となる要素を指定します。
   */
  anchorEl?: Element | null;
  /**
   * Popperの位置を指定します。
   */
  placement?: PopperPlacement;
  /**
   * @see https://popper.js.org/popper-documentation.html#defaults
   */
  options?: PopperOptions;
  /**
   * Popperの開閉を指定します。
   *
   * @default true
   */
  open?: boolean;
  /**
   * 要素の範囲外がクリックされたら発火するCallbackを指定します。
   *
   * @default undefined
   */
  onClickOutside?: (event: MouseEvent | TouchEvent) => void;
}
export interface PopperProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    Pick<PortalProps, "disablePortal">,
    PopperBaseProps {
  children?: React.ReactNode;
}

export const Popper = ({
  anchorEl: anchor,
  children,
  placement = "bottom-start" as const,
  options,
  open = true,
  onClickOutside,
  disablePortal,
  ...others
}: PopperProps) => {
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );

  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (anchor && !anchor.contains(event.target as Node) && onClickOutside) {
        onClickOutside(event);
      }
    },
    [anchor, onClickOutside],
  );
  useOnClickOutside({ current: popperElement }, handleClickOutside);

  const { styles, attributes } = usePopper(anchor, popperElement, {
    placement,
    strategy: options?.strategy,
    onFirstUpdate: options?.onFirstUpdate,
    modifiers: options?.modifiers,
  });

  const { zIndex } = useTheme();

  return anchor && open ? (
    <Portal disablePortal={disablePortal}>
      <div
        role="tooltip"
        ref={setPopperElement}
        css={css`
          z-index: ${zIndex.tooltip};
        `}
        {...others}
        style={{
          ...others?.style,
          ...styles.popper,
        }}
        {...attributes.popper}
      >
        {children}
      </div>
    </Portal>
  ) : null;
};
